const SellingPlanIntegration = (function () {
  "use strict";

  const hiddenClass = "selling_plan_theme_integration--hidden";

  // Get theme settings (will be injected by Liquid)
  const getSettings = () => {
    return (
      window.sellingPlanSettings || {
        displayMode: "dropdown",
        autoSelectSingle: true,
        hideOneTime: false,
        showSavings: true,
        showMonthlyEquivalent: true,
        savingsComparison: "shortest",
        label: "",
        savingsText: "",
        optionFormat: "",
        // Advanced styling
        gradientBorder: false,
        leadingIcon: "",
        chevronIcon: "expand_more",
        // Translations
        translations: {
          day: "day",
          month: "month",
          year: "year",
        },
      }
    );
  };

  class SellingPlansWidget {
    constructor(container) {
      if (!container) {
        return;
      }

      this.container = container;
      this.storedSellingPlans = new Map();
      this.isHandlingChange = false;
      this.isHandlingVariantChange = false;
      this.settings = getSettings();
      this.currentVariantId = null;
      this.plans = new Map(); // Store plan data by variant ID

      this.init();
    }

    init() {
      try {
        // Hide original radio UI if using dropdown
        if (this.settings.displayMode === "dropdown" || this.settings.displayMode === "hidden") {
          this.hideRadioButtons();
        }

        // Always ensure hidden input exists
        this.ensureSellingPlanInput();

        this.setupVariantChangeListeners();

        if (this.settings.displayMode === "dropdown") {
          this.createDropdownUI();
        } else {
          this.listenToSellingPlanFormRadioButtonChange();
        }

        // Get initial variant ID and setup
        const variantInput = document.querySelector(".product-variant-id");
        if (variantInput && variantInput.value) {
          this.currentVariantId = variantInput.value;
          this.handleVariantChange(variantInput.value);
        }
      } catch (error) {
        console.error("Error initializing selling plan widget:", error);
      }
    }

    hideRadioButtons() {
      const radioContainers = this.container.querySelectorAll(".selling_plan_theme_integration");
      radioContainers.forEach((container) => {
        container.style.display = "none";
      });
    }

    createDropdownUI() {
      // Don't create if it already exists
      if (this.container.querySelector(".selling-plan-dropdown-container")) {
        return;
      }

      this.isOpen = false;
      this.selectedIndex = 0;
      this.options = [];

      // Create dropdown container
      const dropdownContainer = document.createElement("div");
      dropdownContainer.className = "selling-plan-dropdown-container";
      dropdownContainer.style.cssText = "margin: 20px 0; position: relative;";

      // Add label (only if label text is set)
      if (this.settings.label) {
        const label = document.createElement("span");
        label.className = "selling-plan-dropdown-label";
        label.textContent = this.settings.label;
        label.style.cssText = `
          display: block;
          margin-bottom: 10px;
          font-weight: 600;
          font-size: var(--sp-dropdown-font-size, 16px);
          color: var(--sp-label-color, #000000);
        `;
        dropdownContainer.appendChild(label);
      }

      // Create custom dropdown wrapper (for gradient border)
      const dropdownWrapper = document.createElement("div");
      dropdownWrapper.className = "selling-plan-dropdown-wrapper";

      if (this.settings.gradientBorder) {
        dropdownWrapper.style.cssText = `
          position: relative;
          padding: var(--sp-border-width, 2px);
          background: linear-gradient(var(--sp-gradient-angle, 135deg), var(--sp-gradient-start, #3b82f6), var(--sp-gradient-end, #8b5cf6));
          border-radius: var(--sp-dropdown-border-radius, 8px);
        `;
      } else {
        dropdownWrapper.style.cssText = `
          position: relative;
        `;
      }

      // Create trigger button
      const trigger = document.createElement("button");
      trigger.type = "button";
      trigger.className = "selling-plan-dropdown-trigger";
      trigger.setAttribute("aria-haspopup", "listbox");
      trigger.setAttribute("aria-expanded", "false");

      const triggerStyles = this.settings.gradientBorder
        ? `
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: var(--sp-dropdown-padding, 12px) 16px;
          border: none;
          border-radius: calc(var(--sp-dropdown-border-radius, 8px) - var(--sp-border-width, 2px));
          font-size: var(--sp-dropdown-font-size, 16px);
          background-color: var(--sp-dropdown-bg-color, #ffffff);
          color: var(--sp-dropdown-text-color, #000000);
          cursor: pointer;
          text-align: left;
          font-family: inherit;
        `
        : `
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: var(--sp-dropdown-padding, 12px) 16px;
          border: var(--sp-border-width, 2px) solid var(--sp-dropdown-border-color, #e0e0e0);
          border-radius: var(--sp-dropdown-border-radius, 8px);
          font-size: var(--sp-dropdown-font-size, 16px);
          background-color: var(--sp-dropdown-bg-color, #ffffff);
          color: var(--sp-dropdown-text-color, #000000);
          cursor: pointer;
          text-align: left;
          font-family: inherit;
        `;
      trigger.style.cssText = triggerStyles;

      // Leading icon
      if (this.settings.leadingIcon) {
        const leadingIcon = document.createElement("span");
        leadingIcon.className = "material-symbols-outlined selling-plan-leading-icon";
        leadingIcon.textContent = this.settings.leadingIcon;
        leadingIcon.style.cssText = `
          color: var(--sp-icon-color, #3b82f6);
          font-size: 24px;
          flex-shrink: 0;
        `;
        trigger.appendChild(leadingIcon);
      }

      // Selected text
      const selectedText = document.createElement("span");
      selectedText.className = "selling-plan-selected-text";
      selectedText.style.cssText = "flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;";
      selectedText.textContent = "Select an option";
      trigger.appendChild(selectedText);

      // Chevron icon
      const chevronIcon = document.createElement("span");
      chevronIcon.className = "material-symbols-outlined selling-plan-chevron";
      chevronIcon.textContent = this.settings.chevronIcon || "expand_more";
      chevronIcon.style.cssText = `
        color: var(--sp-dropdown-text-color, #000000);
        font-size: 24px;
        flex-shrink: 0;
        transition: transform 0.2s ease;
      `;
      trigger.appendChild(chevronIcon);

      // Create options panel
      const optionsPanel = document.createElement("div");
      optionsPanel.className = "selling-plan-options-panel";
      optionsPanel.setAttribute("role", "listbox");
      optionsPanel.style.cssText = `
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 4px;
        background: var(--sp-dropdown-bg-color, #ffffff);
        border: var(--sp-border-width, 2px) solid var(--sp-dropdown-border-color, #e0e0e0);
        border-radius: var(--sp-dropdown-border-radius, 8px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 1000;
        display: none;
        max-height: 300px;
        overflow-y: auto;
      `;

      // Assemble dropdown
      dropdownWrapper.appendChild(trigger);
      dropdownWrapper.appendChild(optionsPanel);
      dropdownContainer.appendChild(dropdownWrapper);

      // Create savings display
      const savingsDisplay = document.createElement("div");
      savingsDisplay.className = "selling-plan-savings";
      savingsDisplay.style.cssText = `
        margin-top: 12px;
        font-size: 14px;
        color: var(--sp-savings-color, #d32f2f);
        font-weight: 600;
      `;

      if (this.settings.showSavings) {
        dropdownContainer.appendChild(savingsDisplay);
      }

      // Insert at beginning of container
      this.container.insertBefore(dropdownContainer, this.container.firstChild);

      // Store references
      this.dropdownTrigger = trigger;
      this.dropdownSelectedText = selectedText;
      this.dropdownChevron = chevronIcon;
      this.dropdownOptionsPanel = optionsPanel;
      this.savingsDisplay = savingsDisplay;
      this.dropdownContainer = dropdownContainer;

      // Event listeners
      this.setupDropdownEventListeners();
    }

    setupDropdownEventListeners() {
      // Toggle dropdown on trigger click
      this.dropdownTrigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDropdown();
      });

      // Close on click outside
      document.addEventListener("click", (e) => {
        if (this.isOpen && !this.dropdownContainer.contains(e.target)) {
          this.closeDropdown();
        }
      });

      // Keyboard navigation
      this.dropdownTrigger.addEventListener("keydown", (e) => {
        this.handleKeyDown(e);
      });
    }

    handleKeyDown(e) {
      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          if (this.isOpen) {
            this.selectOption(this.selectedIndex);
            this.closeDropdown();
          } else {
            this.openDropdown();
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          if (!this.isOpen) {
            this.openDropdown();
          } else {
            this.navigateOptions(1);
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (this.isOpen) {
            this.navigateOptions(-1);
          }
          break;
        case "Escape":
          e.preventDefault();
          this.closeDropdown();
          break;
        case "Tab":
          if (this.isOpen) {
            this.closeDropdown();
          }
          break;
      }
    }

    toggleDropdown() {
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    }

    openDropdown() {
      this.isOpen = true;
      this.dropdownOptionsPanel.style.display = "block";
      this.dropdownTrigger.setAttribute("aria-expanded", "true");
      this.dropdownChevron.style.transform = "rotate(180deg)";
      this.highlightOption(this.selectedIndex);
    }

    closeDropdown() {
      this.isOpen = false;
      this.dropdownOptionsPanel.style.display = "none";
      this.dropdownTrigger.setAttribute("aria-expanded", "false");
      this.dropdownChevron.style.transform = "rotate(0deg)";
    }

    navigateOptions(direction) {
      const newIndex = this.selectedIndex + direction;
      if (newIndex >= 0 && newIndex < this.options.length) {
        this.selectedIndex = newIndex;
        this.highlightOption(newIndex);
      }
    }

    highlightOption(index) {
      const optionElements = this.dropdownOptionsPanel.querySelectorAll(".selling-plan-option");
      optionElements.forEach((opt, i) => {
        if (i === index) {
          opt.style.backgroundColor = "var(--sp-dropdown-border-color, #e0e0e0)";
          opt.scrollIntoView({ block: "nearest" });
        } else {
          opt.style.backgroundColor = "transparent";
        }
      });
    }

    selectOption(index) {
      if (index < 0 || index >= this.options.length) return;

      const option = this.options[index];
      this.selectedIndex = index;

      // Update trigger text
      this.dropdownSelectedText.innerHTML = this.formatOptionTextWithBold(option);

      // Update selected state in options panel
      const optionElements = this.dropdownOptionsPanel.querySelectorAll(".selling-plan-option");
      optionElements.forEach((opt, i) => {
        opt.setAttribute("aria-selected", i === index ? "true" : "false");
      });

      // Trigger change handler
      this.handleDropdownChange(option.id);
    }

    renderOptions(plans) {
      this.options = plans;
      this.dropdownOptionsPanel.innerHTML = "";

      plans.forEach((plan, index) => {
        const option = document.createElement("div");
        option.className = "selling-plan-option";
        option.setAttribute("role", "option");
        option.setAttribute("aria-selected", index === this.selectedIndex ? "true" : "false");
        option.dataset.value = plan.id;
        option.dataset.index = index;
        option.style.cssText = `
          padding: var(--sp-dropdown-padding, 12px) 16px;
          cursor: pointer;
          font-size: var(--sp-dropdown-font-size, 16px);
          color: var(--sp-dropdown-text-color, #000000);
          transition: background-color 0.15s ease;
        `;
        option.innerHTML = this.formatOptionTextWithBold(plan);

        // Hover effect
        option.addEventListener("mouseenter", () => {
          option.style.backgroundColor = "var(--sp-dropdown-border-color, #e0e0e0)";
        });
        option.addEventListener("mouseleave", () => {
          if (this.selectedIndex !== index) {
            option.style.backgroundColor = "transparent";
          }
        });

        // Click to select
        option.addEventListener("click", () => {
          this.selectOption(index);
          this.closeDropdown();
        });

        this.dropdownOptionsPanel.appendChild(option);
      });
    }

    formatOptionTextWithBold(plan) {
      // Get the formatted text and process bold markers
      let text = this.formatOptionText(plan);

      // Convert **text** to <strong>text</strong>
      text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

      return text;
    }

    extractPlanDataFromDOM(variantId) {
      const radioContainer = this.container.querySelector(`[data-variant-id="${variantId}"]`);
      if (!radioContainer) {
        return [];
      }

      const planRadios = radioContainer.querySelectorAll('input[data-radio-type="selling_plan"]');
      if (planRadios.length === 0) {
        return [];
      }

      const plans = Array.from(planRadios).map((radio) => {
        const priceString = radio.dataset.variantPrice || "";
        const priceRaw = radio.dataset.variantPriceRaw;
        const planId = radio.dataset.sellingPlanId;
        const ariaLabel = radio.getAttribute("aria-label") || "";
        const planName = ariaLabel.split(".")[0] || "";

        // Use raw price if available (in cents/pence), otherwise parse from formatted string
        let priceInCents;
        if (priceRaw) {
          priceInCents = parseInt(priceRaw, 10) || 0;
        } else {
          // Fallback: parse from formatted string (less reliable)
          priceInCents = this.parsePrice(priceString) * 100;
        }

        // Extract months from plan name (billing policy data not available in Liquid)
        // Supports: days, weeks, and months (including 3 months for quarterly, 12 months for annually)
        const months = this.extractMonths(planName);

        return {
          id: planId,
          name: planName,
          price: priceString,
          priceRaw: priceInCents, // Store in cents/pence
          months: months,
        };
      });

      // Sort by duration
      plans.sort((a, b) => a.months - b.months);

      // Cache this data
      this.plans.set(variantId, plans);

      return plans;
    }

    populateDropdown(variantId) {
      if (!this.dropdownTrigger) {
        return;
      }

      // Get plan data
      const plans = this.extractPlanDataFromDOM(variantId);

      if (plans.length === 0) {
        this.dropdownContainer.style.display = "none";
        if (this.savingsDisplay) this.savingsDisplay.style.display = "none";
        // Clear hidden input when no plans
        this.updateHiddenInput("");
        return;
      }

      this.dropdownContainer.style.display = "block";
      if (this.savingsDisplay) this.savingsDisplay.style.display = "block";

      // Render options in custom dropdown
      this.renderOptions(plans);

      // Check if we have a stored selection for this variant
      const storedPlan = this.storedSellingPlans.get(variantId);
      const storedIndex = plans.findIndex((p) => p.id === storedPlan);

      if (storedPlan && storedIndex !== -1) {
        this.selectedIndex = storedIndex;
        this.dropdownSelectedText.innerHTML = this.formatOptionTextWithBold(plans[storedIndex]);
        this.updateHiddenInput(storedPlan);
      } else {
        // Auto-select first option
        this.selectedIndex = 0;
        this.dropdownSelectedText.innerHTML = this.formatOptionTextWithBold(plans[0]);
        this.updateHiddenInput(plans[0].id);
        this.storedSellingPlans.set(variantId, plans[0].id);
      }

      // Update aria-selected states
      const optionElements = this.dropdownOptionsPanel.querySelectorAll(".selling-plan-option");
      optionElements.forEach((opt, i) => {
        opt.setAttribute("aria-selected", i === this.selectedIndex ? "true" : "false");
      });

      // Calculate and show savings
      if (this.settings.showSavings && plans.length > 1) {
        this.updateSavingsDisplay(plans);
      } else if (this.savingsDisplay) {
        this.savingsDisplay.textContent = "";
      }
    }

    handleDropdownChange(planId) {
      if (this.isHandlingChange) return;

      try {
        this.isHandlingChange = true;

        // DIRECTLY update the hidden input
        this.updateHiddenInput(planId);

        // Store selection for this variant
        if (this.currentVariantId) {
          this.storedSellingPlans.set(this.currentVariantId, planId);
        }

        // Update savings display
        const plans = this.plans.get(this.currentVariantId);
        if (plans && plans.length > 1) {
          this.updateSavingsDisplay(plans);
        }
      } finally {
        this.isHandlingChange = false;
      }
    }

    updateHiddenInput(planId) {
      // Update ALL hidden inputs with class selected-selling-plan-id
      const inputs = document.querySelectorAll(".selected-selling-plan-id");

      inputs.forEach((input) => {
        input.value = planId || "";
      });

      // Also try to update by name
      const namedInputs = document.querySelectorAll('input[name="selling_plan"]');
      namedInputs.forEach((input) => {
        input.value = planId || "";
      });
    }

    updateSavingsDisplay(plans) {
      if (!this.savingsDisplay || !this.settings.showSavings || plans.length <= 1) {
        if (this.savingsDisplay) this.savingsDisplay.textContent = "";
        return;
      }

      // Get comparison plan based on settings
      let comparisonPlan = null;
      if (this.settings.savingsComparison === "shortest") {
        comparisonPlan = plans[0];
      } else if (this.settings.savingsComparison === "monthly") {
        comparisonPlan = plans.find((p) => p.months === 1) || plans[0];
      }

      if (!comparisonPlan) return;

      // Get selected plan from custom dropdown
      const selectedPlan = this.options[this.selectedIndex];

      if (!selectedPlan || selectedPlan.id === comparisonPlan.id) {
        this.savingsDisplay.textContent = "";
        return;
      }

      this.calculateAndDisplaySavings(selectedPlan, comparisonPlan);
    }

    calculateAndDisplaySavings(selectedPlan, comparisonPlan) {
      if (selectedPlan.months <= comparisonPlan.months) {
        if (this.savingsDisplay) this.savingsDisplay.textContent = "";
        return;
      }

      // Calculate yearly cost for both plans
      // Both priceRaw values are in cents, calculate yearly costs in cents
      const selectedYearlyCostInCents = (selectedPlan.priceRaw / selectedPlan.months) * 12;
      const comparisonYearlyCostInCents = (comparisonPlan.priceRaw / comparisonPlan.months) * 12;

      const savingsInCents = comparisonYearlyCostInCents - selectedYearlyCostInCents;

      if (savingsInCents > 0 && this.savingsDisplay) {
        const savingsFormatted = this.formatPrice(savingsInCents);

        let savingsText = this.settings.savingsText || "";
        savingsText = savingsText.replace("{amount}", savingsFormatted);

        let period = 1;
        let duration = this.settings.translations?.month || "month";

        if (comparisonPlan.months != null) {
          period = comparisonPlan.months;
          duration = this.settings.translations?.month || "month";
        } else if (comparisonPlan.days != null) {
          period = comparisonPlan.days;
          duration = this.settings.translations?.day || "day";
        } else if (comparisonPlan.years != null) {
          period = comparisonPlan.years;
          duration = this.settings.translations?.year || "year";
        }

        savingsText = savingsText.replace("{duration}", duration);
        savingsText = savingsText.replace("{period}", period.toString());

        this.savingsDisplay.textContent = savingsText;
      } else if (this.savingsDisplay) {
        this.savingsDisplay.textContent = "";
      }
    }

    /**
     * Extracts duration in months from plan name
     * Supports: days, weeks, and months (including 3 months for quarterly, 12 months for annually)
     * @param {string} planName - The selling plan name
     * @returns {number} Duration in months
     */
    extractMonths(planName) {
      if (!planName || planName.trim() === "") {
        return 1; // Default to 1 month
      }

      // Try to match months first (e.g., "1 month", "3 months", "12 month")
      const monthMatch = planName.match(/(\d+)\s*month/i);
      if (monthMatch) {
        return parseInt(monthMatch[1], 10);
      }

      // Try to match days (e.g., "7 days", "30 daily")
      const dayMatch = planName.match(/(\d+)\s*(?:day|daily)/i);
      if (dayMatch) {
        const days = parseInt(dayMatch[1], 10);
        return days / 30; // Convert days to months (approximate: 30 days = 1 month)
      }

      // Try to match weeks (e.g., "2 weeks", "4 weekly")
      const weekMatch = planName.match(/(\d+)\s*(?:week|weekly)/i);
      if (weekMatch) {
        const weeks = parseInt(weekMatch[1], 10);
        return (weeks * 7) / 30; // Convert weeks to months (approximate: 30 days = 1 month)
      }

      // Check for keywords without numbers
      if (planName.match(/\bdaily\b|\bday\b/i)) {
        return 1 / 30; // 1 day = ~0.033 months
      } else if (planName.match(/\bweekly\b|\bweek\b/i)) {
        return 7 / 30; // 1 week = ~0.233 months
      } else if (planName.match(/\bmonthly\b|\bmonth\b/i)) {
        return 1; // 1 month
      }

      // Default to 1 month if no pattern matches
      return 1;
    }

    parsePrice(priceString) {
      const match = priceString.match(/[\d,]+\.?\d*/);
      if (!match) return 0;
      return parseFloat(match[0].replace(/,/g, ""));
    }

    formatPrice(amountInCents) {
      // Use Shopify's money format if available (set in main-product.liquid)
      if (window.shopifyMoneyFormat && typeof window.shopifyMoneyFormat.format === "function") {
        return window.shopifyMoneyFormat.format(amountInCents);
      }

      // Fallback to Intl.NumberFormat if Shopify format not available
      const currencyCode = window.shopCurrency || "GBP";
      const locale = window.shopLocale || undefined;
      const amountInMajorUnit = amountInCents / 100;

      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amountInMajorUnit);
      } catch (error) {
        console.warn("Invalid currency code:", currencyCode, error);
        return `${currencyCode} ${amountInMajorUnit.toFixed(2)}`;
      }
    }

    formatOptionText(plan) {
      // Get template from settings or use default
      // Use [[...]] to wrap content that should only appear when showMonthlyEquivalent is true AND months > 1
      // e.g., "{price} [[- only {monthlyPrice}/month]] ({duration})"
      let template = this.settings.optionFormat || "{price} [[- {monthlyPrice}/month]] ({duration})";

      // Calculate monthly price (plan.priceRaw is in cents, so divide by months to get monthly in cents)
      const monthlyPriceInCents = plan.months > 0 ? plan.priceRaw / plan.months : plan.priceRaw;
      const monthlyFormatted = this.formatPrice(monthlyPriceInCents);

      // Format price (plan.priceRaw is in cents)
      const priceFormatted = this.formatPrice(plan.priceRaw);

      // Handle conditional [[...]] blocks BEFORE replacing placeholders
      // Remove content inside [[...]] if showMonthlyEquivalent is false or it's a 1-month plan
      let formattedText = template;
      if (!this.settings.showMonthlyEquivalent || plan.months <= 1) {
        formattedText = formattedText.replace(/\[\[.*?\]\]/g, "");
      } else {
        // Keep the content but remove the [[ ]] markers
        formattedText = formattedText.replace(/\[\[(.*?)\]\]/g, "$1");
      }

      // Replace placeholders
      formattedText = formattedText
        .replace(/\{price\}/g, priceFormatted)
        .replace(/\{monthlyPrice\}/g, monthlyFormatted)
        .replace(/\{months\}/g, plan.months.toString());

      // Clean up empty parentheses, double spaces, or dashes
      formattedText = formattedText
        .replace(/\(\s*\)/g, "") // Remove empty parentheses
        .replace(/\s{2,}/g, " ") // Remove multiple spaces
        .replace(/\s*-\s*-\s*/g, " - ") // Clean up double dashes
        .replace(/\s+-\s*$/g, "") // Remove trailing dash
        .replace(/^\s*-\s+/g, "") // Remove leading dash
        .trim();

      return formattedText;
    }

    setupVariantChangeListeners() {
      const variantInput = document.querySelector(".product-variant-id");
      if (!variantInput) {
        return;
      }

      // Mutation observer for attribute changes
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === "attributes" && mutation.attributeName === "value") {
            const newValue = mutation.target.value;
            if (newValue && newValue !== this.currentVariantId) {
              this.currentVariantId = newValue;
              this.handleVariantChange(newValue);
            }
          }
        }
      });

      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ["value"],
      });

      // Also listen for input/change events
      variantInput.addEventListener("change", () => {
        const newValue = variantInput.value;
        if (newValue && newValue !== this.currentVariantId) {
          this.currentVariantId = newValue;
          this.handleVariantChange(newValue);
        }
      });
    }

    handleVariantChange(variantId) {
      if (this.isHandlingVariantChange) return;

      try {
        this.isHandlingVariantChange = true;

        if (this.settings.displayMode === "dropdown") {
          this.populateDropdown(variantId);
        } else {
          this.handleFormVisibility(variantId);
          this.autoSelectSellingPlan(variantId);
        }
      } finally {
        this.isHandlingVariantChange = false;
      }
    }

    handleFormVisibility(variantId) {
      const allForms = this.container.querySelectorAll(".selling_plan_theme_integration");
      allForms.forEach((form) => {
        if (form.dataset.variantId === variantId) {
          form.classList.remove(hiddenClass);
          if (this.settings.displayMode === "radio") {
            form.style.display = "block";
          }
        } else {
          form.classList.add(hiddenClass);
          form.style.display = "none";
        }
      });
    }

    autoSelectSellingPlan(variantId) {
      if (this.isHandlingChange) return;

      try {
        this.isHandlingChange = true;

        const storedPlan = this.storedSellingPlans.get(variantId);
        if (storedPlan) {
          const radio = this.container.querySelector(`[data-selling-plan-id="${storedPlan}"]`);
          if (radio) {
            radio.checked = true;
            this.updateHiddenInput(storedPlan);
            return;
          }
        }

        // Auto-select first subscription plan
        const subscriptionRadio = this.container.querySelector(
          `[data-variant-id="${variantId}"] input[data-radio-type="selling_plan"]`
        );

        if (subscriptionRadio) {
          subscriptionRadio.checked = true;
          const planId = subscriptionRadio.dataset.sellingPlanId;
          this.updateHiddenInput(planId);

          if (planId) {
            this.storedSellingPlans.set(variantId, planId);
          }
        }
      } finally {
        this.isHandlingChange = false;
      }
    }

    listenToSellingPlanFormRadioButtonChange() {
      const radioButtons = this.container.querySelectorAll('input[name="purchase_option"]');

      radioButtons.forEach((radio) => {
        radio.addEventListener("change", (event) => {
          if (this.isHandlingChange) return;

          try {
            this.isHandlingChange = true;
            const type = event.target.dataset.radioType;
            const planId = event.target.dataset.sellingPlanId;

            this.updateHiddenInput(planId || "");

            if (type === "selling_plan" && planId && this.currentVariantId) {
              this.storedSellingPlans.set(this.currentVariantId, planId);
            }
          } finally {
            this.isHandlingChange = false;
          }
        });
      });
    }

    ensureSellingPlanInput() {
      const variantInput = document.querySelector(".product-variant-id");
      if (!variantInput) return;

      const sectionId = variantInput.dataset.sectionId;
      if (!sectionId) return;

      const form = document.querySelector(`#product-form-${sectionId}`);
      if (!form) return;

      // Check if input already exists
      if (form.querySelector(".selected-selling-plan-id")) {
        return;
      }

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "selling_plan";
      input.classList.add("selected-selling-plan-id");
      input.value = "";
      form.appendChild(input);
    }
  }

  function init() {
    const containers = document.querySelectorAll(".selling_plan_app_container");

    containers.forEach((container) => {
      if (container.dataset.sellingPlanInitialized === "true") {
        return;
      }

      container.dataset.sellingPlanInitialized = "true";
      new SellingPlansWidget(container);
    });
  }

  // Initialize on DOMContentLoaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Also try when page is fully loaded
  if (document.readyState !== "complete") {
    window.addEventListener("load", () => {
      setTimeout(init, 100);
    });
  }

  // Reinitialize when Shopify sections are loaded
  document.addEventListener("shopify:section:load", () => {
    init();
  });

  // Listen for variant changes
  document.addEventListener("variant:change", () => {
    setTimeout(init, 100);
  });

  // Watch for dynamic content changes
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (
              node.classList?.contains("selling_plan_app_container") ||
              node.querySelector?.(".selling_plan_app_container")
            ) {
              setTimeout(init, 100);
            }
          }
        });
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  return { init };
})();
